import { useCallback, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../../components/Button'
import { Panel } from '../../../components/Panel'
import { ImageEditor, type ImageEditorInfo } from '../../../components/ImageEditor'
import { drawImageToTargetCanvas, } from '../../../components/ImageEditor/canvasUtils'
import uploader from '../../../network/uploader'
import api from '../../../network/api'
import { toast } from '../../../services/toastService'
import { useIsNativeApp } from '../../../hooks/useIsNativeApp'

interface TextureOperationPanelProps {
  texture: string | undefined
  handleMessage: (event: { detail: { type: string; data: any } }) => void
  getModelCoverImage: () => Promise<string | undefined>
}

export interface TextureOperationPanelRef {
  submit: () => Promise<void>
  showList: () => void
}

// 常量定义
const DECK_CONFIG = {
  width: 192,
  height: 714,
  aspect: 192 / 714,
  // Canvas 渲染配置
  render: {
    srcWidth: 840,
    deckTop: 59,
    deckLeft: 473,
    bgImage: '/image/3d_deck_left_20260323.png'
  }
}

export const TextureOperationPanel = forwardRef<TextureOperationPanelRef, TextureOperationPanelProps>(
  ({ handleMessage, getModelCoverImage }, ref) => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    // 状态管理
    const [imageData, setImageData] = useState<string | null>(null)
    const [showImageEditor, setShowImageEditor] = useState(false)
    const isNativeApp = useIsNativeApp()

    // 引用管理 (无需触发重绘的数据)
    const saveParamsRef = useRef<any>(undefined)
    const modelImageRef = useRef<string | undefined>(undefined)
    const croppedImageRef = useRef<string | undefined>(undefined)
    const croppedInfoRef = useRef<ImageEditorInfo | undefined>(undefined)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const sendMessage = useCallback((type: string, data: any = {}) => {
      handleMessage({ detail: { type, data } })
    }, [handleMessage])

    // 1. 初始化：从 URL 加载设计
    useEffect(() => {
      const designId = searchParams.get('id')
      if (!designId) return

      const fetchDesign = async () => {
        try {
          const res: any = await api.custom_deck_cover.setOther(designId).setGET().request()
          console.log('fetchDesign >> ', res)
          if (res?.data) {
            const item = res.data
            if (item.originalMedia?.path) setImageData(item.originalMedia.path)
            if (item.modelMedia?.path) {
              modelImageRef.current = item.modelMedia.path
              sendMessage('changeTexture', item.modelMedia.path)
            }
            if (item.croppedMedia?.path) croppedImageRef.current = item.croppedMedia.path
            if (item.croppedInfo) {
              try { croppedInfoRef.current = JSON.parse(item.croppedInfo) } catch (e) { console.error(e) }
            }
          }
        } catch (error) {
          console.error('Fetch design failed:', error)
        }
      }
      fetchDesign()
    }, [searchParams, sendMessage])

    // 2. 图片选择处理
    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string
        setImageData(dataUrl)
        setShowImageEditor(true)
        // 重置旧数据
        croppedInfoRef.current = undefined
        croppedImageRef.current = undefined
        modelImageRef.current = undefined
        saveParamsRef.current = undefined
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    }, [])

    // 3. 图片编辑完成处理
    const handleEditComplete = useCallback(async (editedCanvas: HTMLCanvasElement, editedInfo: ImageEditorInfo) => {
      croppedInfoRef.current = editedInfo

      const { srcWidth, deckTop, deckLeft, bgImage, } = DECK_CONFIG.render
      const canvasWidth = editedCanvas.width * srcWidth / DECK_CONFIG.width
      const canvasSize = { width: canvasWidth, height: canvasWidth }
      const targetPos = {
        x: deckLeft * canvasWidth / srcWidth,
        y: deckTop * canvasWidth / srcWidth
      }

      try {
        const canvasCtx = await drawImageToTargetCanvas(canvasSize, bgImage, editedCanvas, targetPos)
        modelImageRef.current = canvasCtx.toDataURL('image/png')
        croppedImageRef.current = editedCanvas.toDataURL('image/png')
        saveParamsRef.current = undefined

        sendMessage('changeTexture', modelImageRef.current)
        setShowImageEditor(false)
      } catch (e) {
        console.error('Canvas processing failed:', e)
        toast.error('图片处理失败')
      }
    }, [sendMessage])

    // 4. 提交保存
    const _onSubmit = useCallback(async (): Promise<void> => {
      if (!imageData) {
        toast.error('请先选择图片')
        return
      }

      try {
        if (!saveParamsRef.current) {
          const uploadItems = [
            { dataURL: imageData, type: 'original', isGenHash: true },
            ...(croppedImageRef.current ? [{ dataURL: croppedImageRef.current, type: 'cropped', isGenHash: false }] : []),
            ...(modelImageRef.current ? [{ dataURL: modelImageRef.current, type: 'model', isGenHash: false }] : [])
          ]

          // 获取模型正面截图并加入上传列表
          if (getModelCoverImage) {
            const coverImage = await getModelCoverImage()
            // console.log('getModelCoverImage >>> ', coverImage)
            if (coverImage) {
              // saveImageToDisk(coverImage)
              uploadItems.push({ dataURL: coverImage, type: 'cover', isGenHash: false })
            }
          }
          const results = await uploader.uploadBatch(uploadItems)
          const findRes = (type: string) => results.find(r => r.uploadType === type)

          const original = findRes('original')
          const cropped = findRes('cropped')
          const model = findRes('model')
          const cover = findRes('cover')

          saveParamsRef.current = {
            originalUniqueId: original?.uniqueId,
            originalMedia: original ? { path: original.path, type: 'img' } : null,
            coverMedia: cover ? { path: cover.path, type: 'img' } : (cropped ? { path: cropped.path, type: 'img' } : null),
            croppedMedia: cropped ? { path: cropped.path, type: 'img' } : null,
            modelMedia: model ? { path: model.path, type: 'img' } : null,
            croppedInfo: JSON.stringify(croppedInfoRef.current)
          }
          const designId = searchParams.get('id')
          if (designId) {
            saveParamsRef.current.id = Number(designId)
          }
        }
        console.log('custom_deck_cover saveParamsRef.current', saveParamsRef.current)
        await api.custom_deck_cover.request(saveParamsRef.current)
        toast.success('保存成功')
      } catch (error) {
        console.error('Save failed:', error)
        toast.error('保存失败')
      }
    }, [imageData, getModelCoverImage])

    const _onShowList = useCallback(() => {
      navigate('/custom-deck-cover/list', { state: { from: '/custom-deck-cover' } })
    }, [navigate])

    useImperativeHandle(ref, () => ({ submit: _onSubmit, showList: _onShowList }))

    return (
      <>
        <Panel title="纹理操作区" className='pb-10 rounded-b-none rounded-t-2xl bg-black/90'>
          <div className="grid grid-cols-2 gap-2.5 pb-2">
            <Button variant="primary" size="md" fullWidth onClick={() => fileInputRef.current?.click()}>选择图片</Button>
            <Button variant="primary" size="md" fullWidth disabled={!imageData} onClick={() => setShowImageEditor(true)}>编辑图片</Button>
            {!isNativeApp &&
              <Button variant="secondary" size="md" fullWidth onClick={_onShowList}>我的列表</Button>
            }
            {!isNativeApp &&
              <Button variant="primary" size="md" fullWidth disabled={!imageData} onClick={_onSubmit}>提交</Button>
            }
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
        </Panel>

        {showImageEditor && imageData && (
          <div className='fixed inset-0 z-50 flex justify-center items-center bg-black/80'>
            <ImageEditor
              aspect={DECK_CONFIG.aspect}
              imageUrl={imageData}
              editedInfo={croppedInfoRef.current}
              onSave={handleEditComplete}
              onCancel={() => setShowImageEditor(false)}
            />
          </div>
        )}
      </>
    )
  }
)
